import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Rating } from 'react-simple-star-rating';
import { api_getReviews } from '~/backend/api_calls';
import UserSession from '~/backend/user_session';
import { notifyInfo } from '~/components/common/ToastMessages';
import ReviewsModal from '~/components/product/ReviewsModal';
import face5Stars from '~/images/face-5-stars.svg';
import faceDefault from '~/images/face-default.svg';
import faceHalfStars from '~/images/face-half-stars.svg';
import { toggleReviewsModal } from '~/store/reviews/actions';

const ProductReviews = ({ product, handleOpenReviewModal }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isReviewsModalOpen = useSelector(state => state.reviewsReducer.reviewsModalOpen);
	const aggregateRating = product.aggregates?.rating ?? 0;
	const aggregateReviews = product?.aggregates?.reviews ?? 0;
	const [reviews, setReviews] = useState(0);
	const [expandedReviewIds, setExpandedReviewIds] = useState([]);
	const isExpanded = id => expandedReviewIds.includes(id);

	const toggleExpanded = id => {
		setExpandedReviewIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
	};

	const getTruncatedReview = htmlString => {
		const textOnly = htmlString.replace(/<[^>]*>/g, '');
		const words = textOnly.split(/\s+/);
		if (words.length <= 100) return htmlString;
		return words.slice(0, 100).join(' ') + '...';
	};

	const getReviewIcon = rating => {
		let iconSrc = faceDefault;
		if (rating >= 4) iconSrc = face5Stars;
		else if (rating >= 2.5) iconSrc = faceHalfStars;
		return <img src={iconSrc} alt="review-icon" className="review-icon-img" />;
	};

	useEffect(() => {
		const fetchReviews = async () => {
			try {
				const data = await api_getReviews(1, product.id);
				const accepted = {
					...data,
					items: data.items?.filter(review => review.st_nm !== 'Pending') ?? []
				};
				setReviews(accepted);
			} catch (err) {
				console.error(err);
			}
		};
		fetchReviews();
	}, [product]);

	const handleReviewsModal = () => {
		if (!UserSession.isAuthenticated()) {
			notifyInfo(t('review-loginToReview'));
			return;
		}
		document.body.classList.add('toggle-reviews-modal');
		dispatch(toggleReviewsModal(true));
	};

	return (
		<div id="product-reviews">
			<div className="content-wrapper product-reviews-container">
				<h1 className="product-reviews-title">{t('productReviews.title')}</h1>
				{reviews?.items?.length > 0 ? (
					<div className="reviews-container">
						<div className="reviews">
							<h3 className="reviews-heading">{aggregateRating}</h3>
							<div className="star-rating">
								<Rating allowFraction fillColor="black" SVGstrokeColor="#003E92" size={24} readonly initialValue={aggregateRating} />(
								{aggregateReviews})
							</div>
							<button className="rate-button" onClick={handleReviewsModal}>
								{t('productReviews.rateProduct')}
							</button>
						</div>

						<div className="ratings">
							<div className="ratings-container">
								{reviews?.items?.map(review => (
									<div key={review.id} className="ratings-item">
										<div className="ratings-header">
											<span className="ratings-heading">{getReviewIcon(review.rating)}</span>
											<div className="rating-stars">
												<Rating
													allowFraction
													fillColor="black"
													SVGstrokeColor="black"
													size={18}
													style={{ height: '16px' }}
													readonly
													initialValue={review.rating}
												/>
											</div>
										</div>
										<p className="subtext">
											<span className="subtext-1">
												{review.from_name ? (
													<span to={`/readers/${review.from_name}`} className="review-user-link">
														{review.from_name}
													</span>
												) : (
													t('productReviews.anonymous')
												)}
												{' | '}
												{new Date(review.review_date).toLocaleDateString('en-GB', {
													day: '2-digit',
													month: '2-digit',
													year: 'numeric'
												})}
											</span>
										</p>
										<div className="rating-desc">
											{isExpanded(review.id) ? (
												<>
													<div dangerouslySetInnerHTML={{ __html: review.review }} />
													<button onClick={() => toggleExpanded(review.id)} className="see-more-btn">
														{t('productReviews.seeLess')}
													</button>
												</>
											) : (
												<>
													<div dangerouslySetInnerHTML={{ __html: getTruncatedReview(review.review) }} />
													{review.review.split(/\s+/).length > 100 && (
														<button onClick={() => toggleExpanded(review.id)} className="see-more-btn">
															{t('productReviews.seeMore')}
														</button>
													)}
												</>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				) : (
					<>
						<p className="no-reviews-text">{t('productReviews.noReviews')}</p>
						<button className="add-review centered" onClick={handleReviewsModal}>
							{t('productReviews.rateNow')}
						</button>
					</>
				)}
			</div>
			{isReviewsModalOpen && <ReviewsModal product={product} />}
		</div>
	);
};

export default ProductReviews;
